
import React from 'react';
import { Answer, AnswerType, RecommendedAction } from '@/types/assessment';
import { SliderAnswers } from './answer-types/SliderAnswers';
import { MaturityAnswers } from './answer-types/MaturityAnswers';
import { StandardAnswers } from './answer-types/StandardAnswers';
import { useRecommendationsDrawer } from '@/hooks/useRecommendationsDrawer';
import { AnswerRecommendationsDrawer } from './recommendations/AnswerRecommendationsDrawer';

interface QuestionAnswersProps {
  categoryId: string;
  questionId: string;
  answers: Answer[];
  answerType: AnswerType;
  onAddAnswer: (categoryId: string, questionId: string) => void;
  onUpdateAnswer: (categoryId: string, questionId: string, answerId: string, updates: Partial<Answer>) => void;
}

export function QuestionAnswers({ 
  categoryId, 
  questionId, 
  answers, 
  answerType,
  onAddAnswer, 
  onUpdateAnswer 
}: QuestionAnswersProps) {
  const {
    editingAnswerId,
    openRecommendationsDrawer,
    handleOpenChange
  } = useRecommendationsDrawer();

  // Handle recommended actions update
  const handleUpdateRecommendedActions = (answerId: string, recommendedActions: RecommendedAction[]) => {
    onUpdateAnswer(
      categoryId,
      questionId,
      answerId,
      { recommendedActions }
    );
  };

  // Determine which answer component to render based on answer type
  const renderAnswersByType = () => {
    switch (answerType) {
      case 'slider':
        return (
          <SliderAnswers
            categoryId={categoryId}
            questionId={questionId}
            answers={answers}
            onUpdateAnswer={onUpdateAnswer}
          />
        );
      case 'maturity':
        return (
          <MaturityAnswers
            categoryId={categoryId}
            questionId={questionId}
            answers={answers}
            onUpdateAnswer={onUpdateAnswer}
            onOpenRecommendations={openRecommendationsDrawer}
          />
        );
      default:
        return (
          <StandardAnswers
            categoryId={categoryId}
            questionId={questionId}
            answers={answers}
            onAddAnswer={onAddAnswer}
            onUpdateAnswer={onUpdateAnswer}
            onOpenRecommendations={openRecommendationsDrawer}
          />
        );
    }
  };

  return (
    <>
      {renderAnswersByType()}

      {editingAnswerId && (
        <AnswerRecommendationsDrawer
          answer={answers.find(a => a.id === editingAnswerId)!}
          open={!!editingAnswerId}
          onOpenChange={handleOpenChange}
          onSave={(recommendedActions) => handleUpdateRecommendedActions(editingAnswerId, recommendedActions)}
        />
      )}
    </>
  );
}
